/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — Composants UI basiques : implémentation de référence TSX (React)
 * ----------------------------------------------------------------------------
 * Source-of-truth contrats : shared/mte-design/03-component-library-mvp.md §4
 * Source-of-truth tokens    : shared/mte-design/src/styles/tokens.css
 * Source-of-truth placeholders : shared/mte-design/src/components/placeholders.tsx
 *
 * v0.7 — Vaga Visual MTE Shell (D-NX-066, 2026-09-09)
 * Owner : Palette (D-PAL-CL-*, D-PAL-HE-*, D-XR-09)
 *
 * ⚠️  Référence — l'Atelier peut adapter au framework cible (Vue/Svelte/etc.)
 *     tant que les props + états + a11y sont conservés à l'identique.
 *
 * Règles absolues :
 *  - Aucun style en dur (que des `var(--token)`).
 *  - Tap targets ≥ 64×64 px (--touch-lg) sur enfant, ≥ 96×96 (--touch-xl)
 *    sur CTA primaire + cartes héros (D-PAL-CL-03).
 *  - `focus-visible` 3 px + offset 4 px (D-PAL-CL-10).
 *  - Préfixer `aria-label` quand l'élément n'a pas de texte visible.
 *  - `prefers-reduced-motion` déjà géré par tokens.css (durations → 0).
 * ========================================================================== */

import React, {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { Link } from 'react-router-dom';

/* ---------- Tokens helpers ---------------------------------------------- */

type Tone =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'sage-warning'; /* D-PAL-CL-04 — doux-erreur, jamais rouge échec */

type Size = 'sm' | 'md' | 'lg' | 'xl';

type HeroId = 'ninja' | 'lobo' | 'princesa' | 'fada' | 'gato' | 'dragao';

/* === Button ============================================================ */

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  tone?: Tone;
  size?: Size;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  block?: boolean;
  context?: 'enfant' | 'adulte';
  children: ReactNode;
}

/**
 * Bouton principal Palette — toutes les variantes tonales passent par tokens.
 * D-PAL-CL-04 : `danger` = action destructive uniquement (suppression profil).
 * Pour le « doux-erreur », utiliser `tone="sage-warning"`.
 */
export function Button({
  tone = 'primary',
  size = 'md',
  iconStart,
  iconEnd,
  block = false,
  context = 'adulte',
  className = '',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      data-tone={tone}
      data-size={size}
      data-context={context}
      data-block={block || undefined}
      className={`btn btn-${tone} btn-size-${size} focus-ring ${className}`.trim()}
      style={{
        minWidth: context === 'enfant' && size !== 'xl' ? 'var(--touch-lg)' : undefined,
        minHeight: context === 'enfant' && size !== 'xl' ? 'var(--touch-lg)' : undefined,
      }}
      {...rest}
    >
      {iconStart && <span aria-hidden="true" className="btn-icon-start">{iconStart}</span>}
      <span className="btn-label">{children}</span>
      {iconEnd && <span aria-hidden="true" className="btn-icon-end">{iconEnd}</span>}
    </button>
  );
}

/* === Card ============================================================== */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  elevation?: 'flat' | 'sm' | 'md' | 'lg';
  magical?: boolean; /* halo cristal, espace enfant uniquement */
  childSized?: boolean;
  as?: 'div' | 'article' | 'section';
}

/**
 * Conteneur générique. `magical` ajoute le halo cristal — usage enfant héros /
 * chapitre. `childSized` force min-height ≥ 360 px pour lisibilité CE1/CM1.
 */
export function Card({
  elevation = 'sm',
  magical = false,
  childSized = false,
  as: Tag = 'div',
  className = '',
  children,
  ...rest
}: CardProps) {
  return (
    <Tag
      data-elevation={elevation}
      data-magical={magical || undefined}
      data-child-sized={childSized || undefined}
      className={`card card-elev-${elevation} ${magical ? 'card-magical' : ''} ${className}`.trim()}
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-5)',
        boxShadow: `var(--shadow-${elevation})`,
        minHeight: childSized ? 'var(--space-9)' : undefined,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* === ChoiceCard ======================================================== */

export type ChoiceCardVariant = 'mission' | 'diagnostic' | 'resume' | 'quick-replay';

export interface ChoiceCardProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  title: string;
  description?: string;
  illustration?: ReactNode;
  locked?: boolean;
  variant?: ChoiceCardVariant;
  context?: 'enfant' | 'adulte';
  ariaPressed?: boolean;
}

/**
 * Grande carte cliquable pour Entrer, choix héros, choix enfant.
 * D-PAL-CL-03 : tap target 96×96 minimum sur enfant (CTA + carte chapitre).
 */
export function ChoiceCard({
  title,
  description,
  illustration,
  locked = false,
  variant = 'mission',
  context = 'enfant',
  ariaPressed,
  className = '',
  ...rest
}: ChoiceCardProps) {
  return (
    <button
      type="button"
      data-variant={variant}
      data-context={context}
      data-locked={locked || undefined}
      aria-pressed={ariaPressed}
      aria-disabled={locked || undefined}
      className={`choice-card focus-ring ${className}`.trim()}
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        gap: 'var(--space-3)',
        minHeight: 'var(--touch-xl)',
        minWidth: 'var(--touch-xl)',
        maxWidth: '420px',
        width: '100%',
        background: 'var(--surface-card-child)',
        border: '2px solid var(--border-default-child)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-5)',
        boxShadow: 'var(--shadow-sm)',
        textAlign: 'left',
        cursor: locked ? 'not-allowed' : 'pointer',
        opacity: locked ? 0.55 : 1,
      }}
      {...rest}
    >
      {illustration && (
        <div
          aria-hidden="true"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 'var(--space-9)',
          }}
        >
          {illustration}
        </div>
      )}
      <div>
        <div style={{ font: 'var(--weight-semibold) var(--text-lg) var(--font-display)' }}>
          {title}
        </div>
        {description && (
          <div
            style={{
              font: 'var(--weight-regular) var(--text-sm) var(--font-ui)',
              color: 'var(--text-secondary)',
              marginTop: 'var(--space-1)',
            }}
          >
            {description}
          </div>
        )}
      </div>
      {locked && (
        <div
          role="note"
          style={{
            font: 'var(--weight-medium) var(--text-xs) var(--font-ui)',
            color: 'var(--text-tertiary)',
          }}
        >
          🔒 Bientôt disponible
        </div>
      )}
    </button>
  );
}

/* === Layout (Header + Main + Footer) =================================== */

export interface LayoutProps {
  context: 'enfant' | 'adulte';
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  width?: 'narrow' | 'default' | 'wide' | 'child';
}

const widthMap: Record<NonNullable<LayoutProps['width']>, string> = {
  narrow: 'var(--container-narrow)',
  default: 'var(--container-default)',
  wide: 'var(--container-wide)',
  child: 'var(--container-child)',
};

/* === NavHub (M-C2 Workstream C) =============================================== */

const NAV_HUB_LINKS = [
  { to: '/enfant/mes-pouvoirs/', label: 'Mes pouvoirs' },
  { to: '/enfant/aventure/', label: 'Carte du monde' },
  { to: '/enfant/mission/rentree/', label: 'Mission Rentree' },
  { to: '/enfant/diagnostic/', label: 'Reprendre diagnostic' },
  { to: '/famille/accueil/', label: 'Choisis ton profil' },
];

export function NavHub() {
  return (
    <nav aria-label="Navigation hub" className="navhub">
      {NAV_HUB_LINKS.map(function(link) {
        return (
          <Link key={link.to} to={link.to} className="navhub-link mte-button-secondary">
            {link.label}
          </Link>
        );
      })}
    </nav>
  );
}

export function Layout({
  context,
  header,
  footer,
  children,
  width = 'default',
}: LayoutProps) {
  return (
    <div data-context={context} style={{ minHeight: '100vh', display: 'grid', gridTemplateRows: 'auto 1fr auto' }}>
      {header && <header>{header}</header>}
      <main
        style={{
          width: 'min(100% - var(--space-5) * 2, ' + widthMap[width] + ')',
          marginInline: 'auto',
          padding: 'var(--space-6) var(--space-5)',
        }}
      >
        {children}
      </main>
      {footer ? (
        <footer style={{ padding: 'var(--space-5)', borderTop: '1px solid var(--border-subtle)' }}>
          {footer}
        </footer>
      ) : context === 'enfant' ? (
        <footer style={{ padding: 'var(--space-5)', borderTop: '1px solid var(--border-subtle)' }}>
          <NavHub />
        </footer>
      ) : null}
    </div>
  );
}

/* === Header ============================================================ */

export interface HeaderProps {
  logo?: ReactNode;
  nav?: ReactNode;
  context: 'enfant' | 'adulte';
  user?: ReactNode;
}

export function Header({ logo, nav, context, user }: HeaderProps) {
  return (
    <div
      role="banner"
      data-context={context}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-3) var(--space-5)',
        background: 'var(--surface-elevated)',
        borderBottom: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-xs)',
      }}
    >
      {logo && <div style={{ minWidth: 'var(--touch-md)' }}>{logo}</div>}
      {nav && (
        <nav aria-label="Navigation principale" style={{ flex: 1, display: 'flex', gap: 'var(--space-3)' }}>
          {nav}
        </nav>
      )}
      {user && <div>{user}</div>}
    </div>
  );
}

/* === Avatar placeholder héros/Sábio (D-NX-066 P3) ====================== */

export type AvatarKind = HeroId | 'sage' | 'placeholder';

export interface AvatarProps {
  kind: AvatarKind;
  label?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  decorative?: boolean;
}

const sizeMap: Record<NonNullable<AvatarProps['size']>, string> = {
  sm: '32px',
  md: '48px',
  lg: '72px',
  xl: '120px',
};

export function Avatar({
  kind,
  label,
  size = 'md',
  decorative = false,
}: AvatarProps) {
  const ariaProps = decorative
    ? { 'aria-hidden': true as const }
    : { role: 'img' as const, 'aria-label': label ?? kind };
  return (
    <div
      {...ariaProps}
      data-kind={kind}
      data-placeholder="true"
      style={{
        width: sizeMap[size],
        height: sizeMap[size],
        borderRadius: 'var(--radius-pill)',
        background: kind === 'sage'
          ? 'var(--world-crystal-300)'
          : `var(--accent-${kind === 'sage' ? 'sage' : kind}, var(--world-stone-100))`,
        border: '2px solid var(--border-default)',
        display: 'grid',
        placeItems: 'center',
        font: 'var(--weight-semibold) var(--text-md) var(--font-display)',
        color: 'var(--text-on-accent)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* Placeholder abstrait — emoji ou initiale (cf. placeholders.tsx). */}
      <AvatarPlaceholder kind={kind} />
    </div>
  );
}

/** Contenu interne de l'Avatar — centralisé pour testabilité. */
function AvatarPlaceholder({ kind }: { kind: AvatarKind }) {
  switch (kind) {
    case 'ninja':    return <span aria-hidden="true">🥷</span>;
    case 'lobo':     return <span aria-hidden="true">🐺</span>;
    case 'princesa': return <span aria-hidden="true">👸</span>;
    case 'fada':     return <span aria-hidden="true">🧚</span>;
    case 'gato':     return <span aria-hidden="true">🐱</span>;
    case 'dragao':   return <span aria-hidden="true">🐉</span>;
    case 'sage':     return <span aria-hidden="true">🦉</span>;
    case 'placeholder':
    default:
      return (
        <span
          aria-hidden="true"
          style={{
            width: '60%',
            height: '60%',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--world-wood-50)',
            opacity: 0.6,
          }}
        />
      );
  }
}

/* === ProgressBar (jalons 5/10/15) ====================================== */

export interface ProgressBarProps {
  current: number;       /* 0..total */
  total: number;         /* 15 par défaut — Mission Rentrée */
  milestones?: number[]; /* ex. [5,10,15] */
  label?: string;
  context?: 'enfant' | 'adulte';
}

export function ProgressBar({
  current,
  total,
  milestones = [5, 10, 15],
  label,
  context = 'enfant',
}: ProgressBarProps) {
  const pct = Math.min(100, Math.max(0, (current / total) * 100));
  return (
    <div
      role="progressbar"
      aria-valuenow={current}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label={label ?? `Progression ${current} sur ${total}`}
      data-context={context}
      style={{
        width: '100%',
        height: 'var(--touch-sm)',
        background: 'var(--world-wood-100)',
        borderRadius: 'var(--radius-pill)',
        border: '1px solid var(--border-default-child)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: 'linear-gradient(90deg, var(--world-crystal-500), var(--world-light-500))',
          transition: 'width var(--duration-base) var(--ease-out)',
        }}
      />
      {milestones.map((m) => (
        <span
          key={m}
          aria-hidden="true"
          data-milestone={m}
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${(m / total) * 100}%`,
            width: '2px',
            background: m === total ? 'var(--world-light-700)' : 'var(--world-wood-500)',
          }}
        />
      ))}
    </div>
  );
}

/* === EmptyState (D-PAL-CL-11) ========================================== */

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  cta?: ReactNode;
  context?: 'enfant' | 'adulte';
}

export function EmptyState({
  icon,
  title,
  description,
  cta,
  context = 'enfant',
}: EmptyStateProps) {
  return (
    <div
      role="status"
      data-context={context}
      style={{
        display: 'grid',
        placeItems: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-8) var(--space-5)',
        textAlign: 'center',
        maxWidth: 'var(--container-child)',
        margin: '0 auto',
      }}
    >
      {icon && <div style={{ fontSize: 'var(--text-3xl)' }}>{icon}</div>}
      <h2 style={{ font: 'var(--weight-semibold) var(--text-xl) var(--font-display)' }}>
        {title}
      </h2>
      {description && (
        <p
          style={{
            font: 'var(--weight-regular) var(--text-md) var(--font-ui)',
            color: 'var(--text-secondary)',
          }}
        >
          {description}
        </p>
      )}
      {cta}
    </div>
  );
}

/* === ErrorBanner (D-PAL-CL-12) ========================================= */

export interface ErrorBannerProps {
  variant?: 'network' | 'loading' | 'validation' | 'generic';
  title: string;
  description?: string;
  retry?: () => void;
  context?: 'enfant' | 'adulte';
}

export function ErrorBanner({
  variant = 'generic',
  title,
  description,
  retry,
  context = 'enfant',
}: ErrorBannerProps) {
  return (
    <div
      role="alert"
      data-context={context}
      data-variant={variant}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        background: 'var(--surface-card)',
        border: '2px solid var(--state-warning)' /* D-PAL-CL-04 */,
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 'var(--text-lg)' }}>⚠️</span>
      <div style={{ flex: 1 }}>
        <div style={{ font: 'var(--weight-semibold) var(--text-md) var(--font-ui)' }}>{title}</div>
        {description && (
          <div
            style={{
              font: 'var(--weight-regular) var(--text-sm) var(--font-ui)',
              color: 'var(--text-secondary)',
              marginTop: 'var(--space-1)',
            }}
          >
            {description}
          </div>
        )}
      </div>
      {retry && (
        <Button tone="secondary" size="sm" onClick={retry} context={context}>
          Réessayer
        </Button>
      )}
    </div>
  );
}

/* === exports =========================================================== */

export const shellComponents = {
  Button,
  Card,
  ChoiceCard,
  Layout,
  Header,
  Avatar,
  ProgressBar,
  EmptyState,
  ErrorBanner,
};

/* ============================================================================
 * Notes d'intégration Atelier :
 * 1. Importer `tokens.css` UNE fois en haut de l'arbre React (`main.tsx`).
 * 2. Les composants ci-dessus sont stateless — état métier géré par hooks
 *    parents ou state manager (Zustand / Redux / TanStack Query).
 * 3. Adapter les types React en Vue/Svelte : les contrats props restent valides,
 *    seule l'API du framework change.
 * 4. Toujours préfixer `aria-` (cf. §6 de 03-component-library-mvp.md).
 * ========================================================================== */
